import React, { useCallback, useEffect, useContext, useRef, useState } from 'react';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { styles, colors } from '@leapeasy/ui-kit';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  rememberPostLoginReturnPath,
  sanitizeInternalReturnPath,
  shouldRememberPostLoginReturnPath,
} from 'utils/postLoginReturnPath';
import { blocksDashboardAccess } from 'utils/authSession';
import { getRootRedirect } from 'utils/authNavigation';
import { useAuthSession } from 'hooks/useAuthSession';
import { Sidebar } from 'components/Sidebar';
import { Topbar } from 'components/Topbar';
import { BreadCrumbs } from 'components/BreadCrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { primaryMenu, secondaryMenu } from 'data/ui/menu';
import { openMenuItem } from 'store/actions/uiActions';
import { getFlaggedCancelReportAction } from 'store/actions/reportActions';
import { isFlaggedCancellationsUiVisible } from 'utils/flaggedCancellationUi';
import { AppNotification } from 'components/AppNotification';
import { readStoredUser } from 'utils/authSession';
import {
  pathnameMatchesMenuLink,
  resolveMenuItemLink,
  resolveMenuKeyForPathname,
} from 'utils/menuAccess';

export default ({ children, shouldShowCard = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const session = useAuthSession();
  const { token, user: currentUser, isAuthenticated, isSessionComplete } = session;
  const hadActiveSessionRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && currentUser && !blocksDashboardAccess(currentUser)) {
      hadActiveSessionRef.current = true;
    }
  }, [isAuthenticated, currentUser]);

  // update selected menu item when router changes
  const router = useSelector((state) => state.getIn(['router']));
  const activeMenu = useSelector((state) => state.getIn(['ui', 'menuItem'])) || 'dashboard';

  useEffect(() => {
    const pathname = router?.location?.pathname;
    const role = readStoredUser()?.role;
    const aliasMenuKey = resolveMenuKeyForPathname(pathname);

    if (aliasMenuKey && aliasMenuKey !== activeMenu) {
      dispatch(openMenuItem(aliasMenuKey));
      return;
    }

    for (const menuList of [primaryMenu, secondaryMenu]) {
      for (const menuItem of menuList) {
        if (menuItem.child) {
          for (const childItem of menuItem.child) {
            const childLink = resolveMenuItemLink(role, childItem);
            if (pathnameMatchesMenuLink(pathname, childLink)) {
              const newKey = `${menuItem.key}.${childItem.key}`;
              if (newKey !== activeMenu) {
                dispatch(openMenuItem(newKey));
              }
              return;
            }
          }
        } else if (pathnameMatchesMenuLink(pathname, menuItem.link)) {
          if (menuItem.key !== activeMenu) {
            dispatch(openMenuItem(menuItem.key));
          }
          return;
        }
      }
    }
  }, [router, activeMenu, dispatch]);

  // Redirect unauthenticated or incomplete sessions away from the dashboard shell.
  useEffect(() => {
    if (isSessionComplete) return;

    if (!isAuthenticated || !currentUser) {
      if (!shouldRememberPostLoginReturnPath(hadActiveSessionRef.current)) {
        navigate('/login', { replace: true });
        return;
      }

      const pathAndSearch = `${location.pathname}${location.search}`;
      rememberPostLoginReturnPath(pathAndSearch);
      const returnPath = sanitizeInternalReturnPath(pathAndSearch);
      if (returnPath) {
        navigate(`/login?returnUrl=${encodeURIComponent(returnPath)}`, { replace: true });
      } else if (location.pathname === '/login' && location.search) {
        navigate(`${location.pathname}${location.search}`, { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }

    const target = getRootRedirect(session);
    if (target && location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [
    isSessionComplete,
    isAuthenticated,
    currentUser,
    session,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!token || !currentUser) return;
    if (blocksDashboardAccess(currentUser)) return;
    if (!isFlaggedCancellationsUiVisible()) return;
    dispatch(getFlaggedCancelReportAction());
  }, [
    dispatch,
    token,
    currentUser?.require2FA,
    currentUser?.requirePasswordUpdate,
    currentUser?.role,
  ]);

  if (!currentUser) return null;

  return (
    <Container>
      <AppNotification />
      <MainContainer background={colors.purple[100]}>
        <Topbar />
        <BreadCrumbs />
        <Box
          sx={{
            flexGrow: 1,
            margin: '0px 8px 8px 8px',
            ...(shouldShowCard
              ? {
                  padding: '20px 28px',
                  borderRadius: `${styles.borderRadius.large}px`,
                  background: 'white',
                  border: `1px solid ${colors.black[300]}`,
                }
              : {}),
          }}
        >
          {children}
        </Box>
      </MainContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;
  /* min-height: 100vh; */
  & > div:nth-of-type(2) {
    /* height: 100vh; */
    /* overflow: auto; */
  }
`;

const MainContainer = styled.div`
  flex-grow: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.background};
  max-height: 100vh;
  min-height: 100vh;
  overflow: auto;
`;
